import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash, ArrowCounterClockwise, ClockCounterClockwise, GitMerge, MapPin, Warning, Buildings, DownloadSimple } from '@phosphor-icons/react';

// ============================================================
// Corbeille : établissements supprimés, restaurables 30 jours
// ============================================================
export function TrashDialog({ open, onOpenChange, onRestored }) {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const [rEst, rComp] = await Promise.all([
        axios.get(`${API}/establishments/trash`),
        axios.get(`${API}/companies-trash`),
      ]);
      setItems(rEst.data || []);
      setCompanies(rComp.data || []);
    } catch { toast.error('Erreur chargement corbeille'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchTrash(); }, [open, fetchTrash]);

  const handleRestore = async (est) => {
    setRestoringId(est.id);
    try {
      const r = await axios.post(`${API}/establishments/${est.id}/restore`);
      toast.success(`Établissement « ${est.name} » restauré (${r.data.reattached_users} utilisateur(s) rattaché(s))`);
      if (r.data.name_collision) {
        toast.warning(`Attention : un établissement actif porte déjà le nom « ${est.name} » dans cette société. Pensez à les fusionner pour éviter le doublon.`, { duration: 8000 });
      }
      await fetchTrash();
      if (onRestored) onRestored(est);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur restauration'); }
    finally { setRestoringId(null); }
  };

  const handleRestoreCompany = async (comp) => {
    setRestoringId(comp.id);
    try {
      const r = await axios.post(`${API}/companies/${comp.id}/restore`);
      toast.success(`Société « ${comp.name} » restaurée (${r.data.restored_establishments} établissement(s) restauré(s))`);
      if (r.data.name_collision) {
        toast.warning(`Attention : une société active porte déjà le nom « ${comp.name} ».`, { duration: 8000 });
      }
      await fetchTrash();
      if (onRestored) onRestored(comp);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur restauration'); }
    finally { setRestoringId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash size={18} />Corbeille
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">
          Sociétés et établissements supprimés sont conservés 30 jours puis définitivement effacés. La restauration rattache à nouveau utilisateurs et établissements.
        </p>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto">
          {loading && <p className="text-sm text-zinc-400 text-center py-4">Chargement...</p>}
          {!loading && items.length === 0 && companies.length === 0 && (
            <p className="text-sm text-zinc-400 text-center py-8" data-testid="trash-empty">La corbeille est vide</p>
          )}
          {!loading && companies.length > 0 && (
            <div className="space-y-2" data-testid="trash-companies-list">
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Sociétés ({companies.length})</p>
              {companies.map(comp => (
                <div key={comp.id} className="flex items-center justify-between gap-3 p-3 border border-zinc-200 rounded-sm bg-zinc-50" data-testid={`trash-company-${comp.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Buildings size={14} className="text-zinc-400 flex-shrink-0" />
                      <span className="font-medium text-sm truncate">{comp.name}</span>
                      {comp.account_number && <span className="text-[10px] font-mono text-zinc-400">{comp.account_number}</span>}
                      {comp.name_collision && (
                        <span className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm" title="Une société active porte déjà ce nom" data-testid={`collision-badge-company-${comp.id}`}>
                          <Warning size={11} />Nom déjà utilisé
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      {comp.cascade_est_count > 0 ? `${comp.cascade_est_count} établissement(s) en cascade • ` : ''}
                      Supprimée le {comp.deleted_at ? new Date(comp.deleted_at).toLocaleDateString('fr-FR') : '—'}
                      {comp.deleted_by ? ` par ${comp.deleted_by}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-sm ${(comp.days_left ?? 0) <= 5 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {comp.days_left ?? '?'} j restants
                    </span>
                    <Button size="sm" variant="outline" onClick={() => handleRestoreCompany(comp)} disabled={restoringId === comp.id}
                      className="rounded-sm text-xs" data-testid={`restore-company-${comp.id}`}>
                      <ArrowCounterClockwise size={13} className="mr-1" />
                      {restoringId === comp.id ? '...' : 'Restaurer'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2" data-testid="trash-list">
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Établissements ({items.length})</p>
              {items.map(est => (
                <div key={est.id} className="flex items-center justify-between gap-3 p-3 border border-zinc-200 rounded-sm bg-zinc-50" data-testid={`trash-item-${est.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-zinc-400 flex-shrink-0" />
                      <span className="font-medium text-sm truncate">{est.name}</span>
                      {est.ville && <span className="text-xs text-zinc-400">({est.ville})</span>}
                      {est.name_collision && (
                        <span className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm" title="Un établissement actif porte déjà ce nom dans cette société" data-testid={`collision-badge-${est.id}`}>
                          <Warning size={11} />Nom déjà utilisé
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      Société : {est.company_name || '—'} • Supprimé le {est.deleted_at ? new Date(est.deleted_at).toLocaleDateString('fr-FR') : '—'}
                      {est.deleted_by ? ` par ${est.deleted_by}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-sm ${(est.days_left ?? 0) <= 5 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {est.days_left ?? '?'} j restants
                    </span>
                    <Button size="sm" variant="outline" onClick={() => handleRestore(est)} disabled={restoringId === est.id}
                      className="rounded-sm text-xs" data-testid={`restore-est-${est.id}`}>
                      <ArrowCounterClockwise size={13} className="mr-1" />
                      {restoringId === est.id ? '...' : 'Restaurer'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Fusion de deux établissements (doublons)
// ============================================================
export function MergeDialog({ open, onOpenChange, source, candidates, onMerged }) {
  const [targetId, setTargetId] = useState('');
  const [merging, setMerging] = useState(false);

  useEffect(() => { if (open) setTargetId(''); }, [open]);

  const options = (candidates || []).filter(e => e.id !== source?.id);
  const target = options.find(e => e.id === targetId);

  const handleMerge = async () => {
    if (!targetId) { toast.error('Sélectionnez l\'établissement de destination'); return; }
    if (!window.confirm(`Fusionner « ${source.name} » dans « ${target?.name} » ?\n\nLes commandes et utilisateurs de « ${source.name} » seront transférés, puis « ${source.name} » sera supprimé définitivement. Cette action est irréversible.`)) return;
    setMerging(true);
    try {
      const r = await axios.post(`${API}/establishments/${targetId}/merge`, { source_id: source.id });
      toast.success(`Fusion réussie : ${r.data.orders_moved} commande(s) et ${r.data.users_moved} utilisateur(s) transférés vers « ${target?.name} »`);
      onOpenChange(false);
      if (onMerged) onMerged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur fusion'); }
    finally { setMerging(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge size={18} />Fusionner « {source?.name} »
          </DialogTitle>
        </DialogHeader>
        {options.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4" data-testid="merge-no-candidates">
            Aucun autre établissement dans cette société : la fusion nécessite au moins deux établissements.
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500">Fusionner dans l'établissement *</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger className="rounded-sm mt-1 bg-white" data-testid="merge-target-select">
                  <SelectValue placeholder="Choisir la destination" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {options.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}{e.ville ? ` (${e.ville})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-[12px] text-amber-800 flex gap-2">
              <Warning size={16} className="flex-shrink-0 mt-0.5" />
              <span>
                Les <strong>commandes</strong> et <strong>utilisateurs</strong> de « {source?.name} » seront transférés vers la destination,
                puis « {source?.name} » sera <strong>supprimé définitivement</strong> (pas de passage par la corbeille).
              </span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          {options.length > 0 && (
            <Button onClick={handleMerge} disabled={merging || !targetId} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="confirm-merge-btn">
              {merging ? 'Fusion...' : 'Fusionner'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Historique des modifications (société + établissements)
// ============================================================
const ACTION_STYLES = {
  create: { label: 'Création', cls: 'bg-emerald-100 text-emerald-700' },
  update: { label: 'Modification', cls: 'bg-blue-100 text-blue-700' },
  delete: { label: 'Suppression', cls: 'bg-red-100 text-red-700' },
  restore: { label: 'Restauration', cls: 'bg-amber-100 text-amber-700' },
  merge: { label: 'Fusion', cls: 'bg-purple-100 text-purple-700' },
  user_attach: { label: 'Utilisateur lié', cls: 'bg-zinc-100 text-zinc-600' },
};

export function HistoryDialog({ open, onOpenChange, company }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open || !company?.id) return;
    setLoading(true);
    axios.get(`${API}/account-history`, { params: { company_id: company.id, limit: 200 } })
      .then(r => setItems(r.data || []))
      .catch(() => toast.error('Erreur chargement historique'))
      .finally(() => setLoading(false));
  }, [open, company?.id]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/account-history/export`, {
        params: { company_id: company.id },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      const safeName = (company?.name || 'compte').replace(/[^A-Za-z0-9_-]+/g, '_');
      a.download = `historique_${safeName}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch { toast.error('Erreur export CSV'); }
    finally { setExporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-6">
            <span className="flex items-center gap-2">
              <ClockCounterClockwise size={18} />Historique — {company?.name}
            </span>
            <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={exporting || loading || items.length === 0}
              className="rounded-sm text-xs font-normal" data-testid="export-history-csv-btn">
              <DownloadSimple size={13} className="mr-1" />
              {exporting ? 'Export...' : 'Exporter CSV'}
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="history-list">
          {loading && <p className="text-sm text-zinc-400 text-center py-4">Chargement...</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-zinc-400 text-center py-8" data-testid="history-empty">
              Aucun événement enregistré pour ce compte (le journal démarre à partir de maintenant).
            </p>
          )}
          {items.map(ev => {
            const style = ACTION_STYLES[ev.action] || { label: ev.action, cls: 'bg-zinc-100 text-zinc-600' };
            return (
              <div key={ev.id} className="p-3 border border-zinc-200 rounded-sm bg-white" data-testid={`history-item-${ev.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${style.cls}`}>{style.label}</span>
                  <span className="text-[10px] text-zinc-400 uppercase">{ev.entity_type === 'company' ? 'Société' : 'Établissement'}</span>
                  <span className="text-xs font-medium text-zinc-700">{ev.entity_name}</span>
                  <span className="text-[11px] text-zinc-400 ml-auto">
                    {ev.created_at ? new Date(ev.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                  </span>
                </div>
                <p className="text-xs text-zinc-600 mt-1">{ev.details}</p>
                {ev.actor_name && <p className="text-[11px] text-zinc-400 mt-0.5">Par {ev.actor_name}</p>}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
